import { forwardRef, ComponentPropsWithoutRef } from "react";

import { LayoutUtilProps } from "../../../types";

import { LinkHeadless } from "./LinkHeadless";

import { useLinkStyles } from "../useLinkStyles";

/**
 * Props for the Link component.
 * @extends ComponentPropsWithoutRef<"a">
 * @extends LayoutUtilProps
 */
export type LinkProps = ComponentPropsWithoutRef<"a"> &
  LayoutUtilProps &
  (
    | {
        /**
         * The visual appearance of the link.
         * @default "primary"
         */
        appearance?: "primary";
        /**
         * Whether the link should have a quieter, less prominent appearance
         * @remarks Only available when appearance is "primary".
         */
        ghost?: boolean;
      }
    | {
        /**
         * The visual appearance of the LinkButton. Secondary appearance cannot be ghost.
         */
        appearance: "secondary";
        /**
         * Whether the LinkButton should have a quieter, less prominent appearance.
         * @remarks Must be false when appearance is "secondary".
         */
        ghost?: false;
      }
  );

/**
 * Link component for creating accessible, styled hyperlinks.
 *
 * Features:
 * - Two visual appearances: primary (default) and secondary
 * - Ghost mode for less prominent styling
 * - Automatic external link icon for target="_blank" links
 * - Security attributes (rel="noopener noreferrer") for external links
 * - Full accessibility support with proper focus management
 * - Supports layout utilities for positioning and spacing
 * - Flexible content support for text and other elements
 * - Responsive design with consistent typography
 * - Hover and focus states with proper visual feedback
 * - Screen reader support for external link announcements
 *
 * @example
 * <Link href="/dashboard" appearance="primary">
 *   Go to Dashboard
 * </Link>
 *
 * @example
 * <Link href="https://example.com" target="_blank" ghost>
 *   External Documentation
 * </Link>
 *
 * @example
 * <Link href="/settings" appearance="secondary">
 *   Settings
 * </Link>
 *
 * @example
 * <Link href="/profile" gap="2" alignItems="center">
 *   <Avatar name="John Doe" size="small" />
 *   View Profile
 * </Link>
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const { appearance, className, ghost, style, children, ...rest } = props;
  const { linkClassNames, styleCombined } = useLinkStyles({
    appearance,
    className,
    ghost,
    style,
  });

  return (
    <LinkHeadless
      className={linkClassNames}
      data-anv="link"
      ref={ref}
      style={styleCombined}
      {...rest}
    >
      {children}
    </LinkHeadless>
  );
});

Link.displayName = "Link";
